import type { CampaignCopy } from "./copy";

/** Shared, public configuration. Never place secrets or recipient data here. */
export interface CampaignConfig {
	id: string;
	codes: readonly string[];
	discountPercent: number;
	/** Only rails whose exact identity survives callbacks are supported. */
	allowedPaymentMethods: readonly ("vorkasse" | "crypto")[];
	allowPointsOnlyOrders: boolean;
	startsAt: string;
	endsAt: string;
	channels: readonly string[];
	copy: Readonly<Record<"de" | "en", CampaignCopy>>;
}

/** Opt-in only: adding a reviewed campaign is a normal code/release change. */
export const CAMPAIGNS: readonly CampaignConfig[] = [];
export const CAMPAIGN_PAYMENT_BLOCKED_REASON = "campaign_payment_method_blocked";

export function getActiveCampaigns(
	now: Date,
	channel?: string | null,
	campaigns = CAMPAIGNS,
): readonly CampaignConfig[] {
	const time = now.getTime();
	return campaigns.filter(
		(campaign) =>
			time >= Date.parse(campaign.startsAt) &&
			time <= Date.parse(campaign.endsAt) &&
			(!channel || campaign.channels.includes(channel)),
	);
}

export function findActiveCampaign(
	voucherCode: string | null | undefined,
	now: Date,
	channel?: string | null,
	campaigns = CAMPAIGNS,
): CampaignConfig | undefined {
	if (!voucherCode?.trim()) return undefined;
	const code = voucherCode.trim().toUpperCase();
	return getActiveCampaigns(now, channel, campaigns).find((campaign) =>
		campaign.codes.some((value) => value.trim().toUpperCase() === code),
	);
}

/** Exact checkout method IDs; missing channel is conservative (all configured channels). */
export function isPaymentMethodAllowed(
	method: string,
	code: string | null | undefined,
	now: Date,
	channel?: string | null,
	campaigns = CAMPAIGNS,
): boolean {
	const campaign = findActiveCampaign(code, now, channel, campaigns);
	return (
		!campaign ||
		(method === "free_order"
			? campaign.allowPointsOnlyOrders
			: (campaign.allowedPaymentMethods as readonly string[]).includes(method))
	);
}

export function isCampaignRefusal(value: unknown): boolean {
	if (typeof value === "string") {
		try {
			value = JSON.parse(value);
		} catch {
			return false;
		}
	}
	if (!value || typeof value !== "object") return false;
	const record = value as Record<string, unknown>;
	return (record.reason ?? record.code) === CAMPAIGN_PAYMENT_BLOCKED_REASON;
}
